import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  FacebookShareButton,
  VKShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  TelegramShareButton,
  WhatsappShareButton,
  EmailShareButton,
  ViberShareButton,
  FacebookIcon,
  LinkedinIcon,
  VKIcon,
  TelegramIcon,
  WhatsappIcon,
  EmailIcon,
  ViberIcon,
  XIcon,
} from 'react-share';
import { useDispatch, useSelector } from 'react-redux';

import { CloseButton, Loader } from 'components';
import { ShareIcon } from 'static';
import { getCurrentCommunity } from 'store/selectors/currentCommunity';
import { closeModal } from 'store/actionCreators/modal';
import { useOutsideClick } from 'hooks';
import { getShareLink } from 'services/events';

import {
  StyledPopup,
  Wrapper,
  ShareButtons,
  StyledInvitationLink,
  Text,
  IconWrapper,
  PopupWrapper,
  Title,
} from './styled';

interface SharePopupProps {
  eventId?: string;
  communityId?: string;
}

const SharePopup: FC<SharePopupProps> = ({
  eventId,
  communityId: propCommunityId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { communityId } = useParams();
  const wrapperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>();
  const [open, setOpen] = useState<boolean>(true);
  const [loaded, setLoaded] = useState(false);
  const [url, setUrl] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState(null);
  const community = useSelector(getCurrentCommunity);

  // Function to fetch event data by trying different community IDs
  const fetchEventData = async (eventId: string, communityId?: string) => {
    try {
      // If we have a specific community ID, try that first
      if (communityId) {
        return await getShareLink(communityId, eventId);
      }
      // If no community ID, we need to handle this case
      // For now, throw an error to indicate we need the community ID
      throw new Error('Community ID is required but not available');
    } catch (error) {
      console.error('Error fetching event data:', error);
      throw error;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (eventId) {
          // Use communityId from props first, then fallback to other sources
          const effectiveCommunityId =
            propCommunityId || community?.id || communityId;
          if (!effectiveCommunityId) {
            setError(
              'Unable to determine community ID. Please ensure you are accessing this from a valid community context.',
            );
            setLoaded(true);
            return;
          }
          const data = await fetchEventData(eventId, effectiveCommunityId);
          setUrl(data.shareLink.shortLink);
          setEmailData({ subject: data.subject, body: data.body });
        } else {
          setUrl(community?.inviteLink);
        }
      } catch (error) {
        console.error('Failed to fetch share data:', error);
        setError('Failed to load sharing data. Please try again.');
      } finally {
        setLoaded(true);
      }
    })();
  }, [eventId, propCommunityId, community, communityId]);

  const subject = useMemo(() => {
    if (eventId) {
      return emailData?.subject || null;
    }

    return `${t('communityInvitation.subjectFirst')} ${
      community?.name || ''
    } ${t('communityInvitation.subjectSecond')} - Gumb`;
  }, [eventId, emailData, community]);

  const body = useMemo(() => {
    if (eventId) {
      return emailData?.body || null;
    }

    return t('communityInvitation.body');
  }, [eventId, emailData]);

  const title = eventId
    ? null
    : `${t('communityInvitation.subjectFirst', {
        returnObjects: true,
      })}\n\n${community?.name || ''}\n${t(
        'communityInvitation.subjectSecond',
      )} - Gumb`;

  const copyToClipboard = async () => {
    const value = inputRef.current.value;

    await navigator.clipboard.writeText(value);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 1500);
  };

  const closePopup = () => {
    setOpen(false);
    dispatch(closeModal());
  };

  useOutsideClick(wrapperRef, closePopup);

  if (!open) {
    return null;
  }

  return (
    <Wrapper>
      <StyledPopup ref={wrapperRef}>
        {!loaded ? (
          <Loader />
        ) : error ? (
          <PopupWrapper>
            <CloseButton onClick={closePopup} />
            <Title>Error</Title>
            <Text
              style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}
            >
              {error}
            </Text>
          </PopupWrapper>
        ) : (
          <PopupWrapper>
            <CloseButton onClick={closePopup} />
            <input type="hidden" value={url} ref={inputRef} />
            <Title>{t('common.shareEventTitle')}</Title>
            <ShareButtons>
              <IconWrapper>
                <EmailShareButton
                  url={url}
                  subject={subject}
                  body={body}
                  separator={'\n\n'}
                >
                  <EmailIcon size={42} round />
                  <Text>Email</Text>
                </EmailShareButton>
              </IconWrapper>
              <IconWrapper>
                <WhatsappShareButton
                  url={url}
                  title={title}
                  separator={eventId ? null : '\n\n'}
                >
                  <WhatsappIcon size={42} round />
                  <Text>Whatsapp</Text>
                </WhatsappShareButton>
              </IconWrapper>
              <IconWrapper>
                <TelegramShareButton url={url} title={title}>
                  <TelegramIcon size={42} round />
                  <Text>Telegram</Text>
                </TelegramShareButton>
              </IconWrapper>
              <IconWrapper>
                <FacebookShareButton url={url} title={title}>
                  <FacebookIcon size={42} round />
                  <Text>Facebook</Text>
                </FacebookShareButton>
              </IconWrapper>
              <IconWrapper>
                <TwitterShareButton url={url} title={title}>
                  <XIcon size={42} round />
                  <Text>X</Text>
                </TwitterShareButton>
              </IconWrapper>
              <IconWrapper>
                <LinkedinShareButton url={url} title={title}>
                  <LinkedinIcon size={42} round />
                  <Text>Linkedin</Text>
                </LinkedinShareButton>
              </IconWrapper>
              <IconWrapper>
                <VKShareButton url={url} title={title}>
                  <VKIcon size={42} round />
                  <Text>VK</Text>
                </VKShareButton>
              </IconWrapper>
              <IconWrapper>
                <ViberShareButton
                  url={url}
                  title={title}
                  separator={eventId ? null : '\n\n'}
                >
                  <ViberIcon size={42} round />
                  <Text>Viber</Text>
                </ViberShareButton>
              </IconWrapper>
            </ShareButtons>
            <Title padding={'24px'}>{t('common.copyEventLink')}</Title>
            <StyledInvitationLink
              onClick={copyToClipboard}
              linkCopied={linkCopied}
            >
              {linkCopied ? t('common.copied') : t('common.copyLink')}
              <ShareIcon />
            </StyledInvitationLink>
          </PopupWrapper>
        )}
      </StyledPopup>
    </Wrapper>
  );
};

export default SharePopup;
